import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface State {
  error: Error | null;
  info: ErrorInfo | null;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ error, info });
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info);
  }

  reset = () => this.setState({ error: null, info: null });

  render() {
    if (this.state.error) {
      return (
        <div className="p-6 max-w-4xl mx-auto">
          <Card>
            <CardHeader className="flex flex-row items-center gap-3 space-y-0">
              <div className="rounded-full bg-destructive/10 p-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
              </div>
              <CardTitle>Erro ao renderizar esta tela</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Algo deu errado e a página não pôde ser exibida. Os detalhes abaixo ajudam a reportar o problema.
              </p>
              <pre className="text-xs bg-muted p-3 rounded overflow-auto max-h-60 font-mono">
                {this.state.error.message}
                {this.state.info?.componentStack && '\n\nComponent stack:' + this.state.info.componentStack}
              </pre>
              <div className="flex gap-2">
                <Button onClick={this.reset} variant="outline">
                  <RefreshCcw className="h-4 w-4 mr-1" /> Tentar novamente
                </Button>
                <Button onClick={() => (window.location.href = '/')}>Ir para o início</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }
    return this.props.children;
  }
}
